import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderIcon, PlusIcon, EllipsisVerticalIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { boardsAPI } from '../utils/api';
import toast from 'react-hot-toast';

const BoardsPage = ({ user }) => {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [boards, setBoards] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    board_type: 'general'
  });

  const sampleBoards = [
    {
      id: 'sample-1',
      name: 'YouTube Strategy',
      description: 'Content planning and competitor analysis for YouTube channel',
      itemCount: 24,
      lastUpdated: '2 hours ago',
      color: 'bg-red-500',
      isDemo: true,
    },
    {
      id: 'sample-2',
      name: 'Instagram Content',
      description: 'Reels, stories, and post ideas for Instagram growth',
      itemCount: 18,
      lastUpdated: '1 day ago',
      color: 'bg-pink-500',
      isDemo: true,
    },
    {
      id: 'sample-3',
      name: 'Competitor Analysis',
      description: 'Tracking competitor content and strategies',
      itemCount: 32,
      lastUpdated: '3 days ago',
      color: 'bg-blue-500',
      isDemo: true,
    },
    {
      id: 'sample-4',
      name: 'TikTok Trends',
      description: 'Latest TikTok trends and viral content ideas',
      itemCount: 15,
      lastUpdated: '1 week ago',
      color: 'bg-purple-500',
      isDemo: true,
    },
  ];

  // Fetch real boards from API
  const fetchBoards = async () => {
    try {
      setBoardsLoading(true);
      const response = await boardsAPI.getBoards();
      const realBoards = response.data.boards.map(board => ({
        ...board,
        itemCount: 0, // We'd need to fetch this separately
        lastUpdated: new Date(board.updated_at).toLocaleDateString(),
        color: getRandomColor(),
        isDemo: false,
      }));
      setBoards(realBoards);
    } catch (error) {
      console.error('Failed to fetch boards:', error);
      toast.error('Failed to load boards');
      // If API fails, we'll show sample boards only
      setBoards([]);
    } finally {
      setBoardsLoading(false);
    }
  };

  // Get random color for new boards
  const getRandomColor = () => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-red-500', 'bg-indigo-500'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  useEffect(() => {
    fetchBoards();
  }, []);

  const handleCreateBoard = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await boardsAPI.createBoard(formData);
      toast.success('Board created successfully!');
      setShowCreateModal(false);
      setFormData({ name: '', description: '', board_type: 'general' });
      
      // Refresh the boards list
      await fetchBoards();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create board');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBoardClick = (board) => {
    // Navigate to board detail page
    navigate(`/boards/${board.id}`);
  };

  // Combine real boards with sample boards for demo
  const allBoards = [...boards, ...sampleBoards];

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Content Boards</h1>
          <p className="mt-2 text-gray-600">
            Organize your content ideas and research into focused boards
          </p>
          {boards.length > 0 && (
            <p className="mt-1 text-sm text-green-600">
              ✅ {boards.length} real board{boards.length !== 1 ? 's' : ''} created!
            </p>
          )}
        </div>
        <button 
          className="btn-primary flex items-center"
          onClick={() => setShowCreateModal(true)}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Board
        </button>
      </motion.div>

      {/* Loading State */}
      {boardsLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading your boards...</p>
        </div>
      )}

      {/* Boards Grid */}
      {!boardsLoading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {allBoards.map((board, index) => (
            <motion.div
              key={board.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer group"
              onClick={() => handleBoardClick(board)}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${board.color}`}>
                  <FolderIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex items-center space-x-2">
                  {board.isDemo && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                      Demo
                    </span>
                  )}
                  {!board.isDemo && (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                      Real
                    </span>
                  )}
                  <button 
                    className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-100 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      toast.info('Board menu coming soon!');
                    }}
                  >
                    <EllipsisVerticalIcon className="h-5 w-5 text-gray-400" />
                  </button>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {board.name}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {board.description || 'No description provided'}
              </p>
              
              <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                <span>{board.itemCount} items</span>
                <span>Updated {board.lastUpdated}</span>
              </div>
              
              {/* Board Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/boards/${board.id}`);
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  📋 List View
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/whiteboard/${board.id}`);
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  🎨 Whiteboard
                </button>
              </div>
            </motion.div>
          ))}

          {/* Create New Board Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="card border-2 border-dashed border-gray-300 hover:border-primary-500 hover:bg-primary-50 transition-colors duration-200 cursor-pointer flex items-center justify-center min-h-[200px]"
            onClick={() => setShowCreateModal(true)}
          >
            <div className="text-center">
              <PlusIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                Create new board
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Start organizing your content
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Updated Demo Notice */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <FolderIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              MVP Demo Mode
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                🎯 <strong>Real boards</strong> (marked "Real") are created with your account and stored in the database.
                <br />
                📋 <strong>Demo boards</strong> (marked "Demo") are sample content for demonstration.
                <br />
                ✨ Click any board to interact with it!
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Create Board Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Board</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreateBoard} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Board Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="e.g., YouTube Strategy"
                />
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optional)
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="input-field"
                  placeholder="What will you organize in this board?"
                />
              </div>
              
              <div>
                <label htmlFor="board_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Board Type
                </label>
                <select
                  id="board_type"
                  name="board_type"
                  value={formData.board_type}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="general">General</option>
                  <option value="competitor">Competitor Analysis</option>
                  <option value="trending">Trending Content</option>
                </select>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.name.trim()}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Board'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardsPage; 